import { useState, useEffect } from 'react';
import { Flex, Autocomplete, TextField, Icon, Spinner } from '@scale/scaleui-radix';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { IListItem } from 'react-scale/components/WebinarSessions/types';
import {
  useFetchProjectQualifications,
  useLookupQualificationsById,
} from 'react-scale/components/WebinarSessions/api';
import _ from 'lodash';

interface ProjectQualificationAutocompleteProps {
  projectId: string;
  qualificationIds: string[];
  onQualificationIdsChange: (qualificationIds: string[]) => void;
}

export const ProjectQualificationAutocomplete = ({
  projectId,
  qualificationIds,
  onQualificationIdsChange,
}: ProjectQualificationAutocompleteProps) => {
  const [searchInput, setSearchInput] = useState('');
  const [fetchedQualifications, setFetchedQualifications] = useState<Record<string, string>>({});

  // Fetches the qualifications to show up as options based on the search input
  const { data: projectQualificationsData, isFetching: isFetchingProjectQualifications } =
    useFetchProjectQualifications({
      projectId,
    });
  const projectQualificationsOptions = projectQualificationsData?.map(
    (projectQualification: IListItem) => ({
      value: projectQualification.id,
      label: projectQualification.name,
    }),
  );

  // Fetches the names for the currently selected qualifications
  const qualificationIdsToFetch = qualificationIds.filter(id => !fetchedQualifications[id]);
  const { data: newQualifications, isFetching: isFetchingNewQualifications } =
    useLookupQualificationsById({
      qualificationIds: qualificationIdsToFetch,
    });

  useEffect(() => {
    if (newQualifications && Object.keys(newQualifications).length > 0) {
      const qualificationNamesById: Record<string, string> = newQualifications.reduce(
        (acc, item: IListItem) => {
          acc[item.id] = item.name;
          return acc;
        },
        {} as Record<string, string>,
      );
      setFetchedQualifications(prev => ({
        ...prev,
        ...qualificationNamesById,
      }));
    }
  }, [newQualifications]);

  // TODO: Once Radix is migrated to 1.36, we can use the AsyncItems and AsyncValues to replace the loading state
  return (
    <Autocomplete.Root
      multiple
      search={searchInput}
      onSearchChange={setSearchInput}
      value={qualificationIds}
      onValueChange={(newQualificationIds: string[]) => {
        if (!_.isEqual(newQualificationIds, qualificationIds)) {
          onQualificationIdsChange(newQualificationIds);
        }
      }}
    >
      <Autocomplete.Content>
        <Autocomplete.TextField
          placeholder="Search by qualification name..."
          // Do not allow invalid values from paste
          onPaste={() => {}}
        >
          <TextField.Slot side="left">
            <Icon icon={faSearch} label="Search" />
          </TextField.Slot>
          <Autocomplete.Values>
            {qualificationIds.map(qualificationId => (
              <Autocomplete.Value key={qualificationId} value={qualificationId}>
                {isFetchingNewQualifications && !fetchedQualifications[qualificationId] ? (
                  <Spinner />
                ) : (
                  fetchedQualifications[qualificationId]
                )}
              </Autocomplete.Value>
            ))}
          </Autocomplete.Values>
          <Autocomplete.Reset />
        </Autocomplete.TextField>

        <Autocomplete.Items>
          {isFetchingProjectQualifications
            ? [
                <Autocomplete.Item
                  key={'fetching-qualifications'}
                  keywords={searchInput}
                  value={searchInput}
                  disabled
                  style={{ pointerEvents: 'none' }}
                >
                  <Flex direction="row" gap="2">
                    <Spinner />
                    {'Fetching qualifications...'}
                  </Flex>
                </Autocomplete.Item>,
              ]
            : (projectQualificationsOptions ?? []).map(qualification => (
                <Autocomplete.Item key={qualification.value} value={qualification.value}>
                  {qualification.label}
                </Autocomplete.Item>
              ))}
        </Autocomplete.Items>
      </Autocomplete.Content>
    </Autocomplete.Root>
  );
};
